import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/Dialog'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useEditWorkspaceModalValues,
  useCloseEditWorkspaceModal,
} from './state'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUpdateWorkspaceMutation } from '../../mutations'
import { useWorkspaceList } from '~/app/(dashboard)/dashboardQuery'
import { SearchableInputField } from '~/components/SearchableInputField'
import { useDashboardWorkspaceContext } from '~/app/(dashboard)/dashboardContext'
import { WorkspacListQuery } from '~/gql/graphql'
import { EditWorkspaceIcon } from '../icons'
import { useTranslation } from '~/i18n/useTranslation'

type WorkspaceListingItem = Pick<
  WorkspacListQuery['getWorkspacesForMember'][0],
  'id' | 'commonName'
>

const formSchema = z.object({
  parent: z
    .object({
      id: z.string(),
      commonName: z.string(),
    })
    .nullable(),
  workspaceName: z
    .string()
    .min(3, 'Name must be at least 3 characters long.')
    .max(200, 'Name cannot be greater than 200 characters.'),
  projectCode: z
    .string()
    .max(200, 'Project code cannot be greater than 200 characters.')
    .optional(),
})

type FormSchema = z.infer<typeof formSchema>

const defaultValues = {
  parent: null,
  workspaceName: '',
  projectCode: '',
}

export function EditWorkspaceModal() {
  const { t } = useTranslation()
  const dashboardWorkspaceContext = useDashboardWorkspaceContext()
  const response = useWorkspaceList(dashboardWorkspaceContext.memberId || '')
  const memberWorkspaces = useMemo(
    () => response?.data?.getWorkspacesForMember ?? [],
    [response?.data?.getWorkspacesForMember]
  )
  // The dashboardWorkspaceContext.workspace attribute values seem different than
  // the workspace in getWorkspacesForMember which should be correct.
  // So fetching the active workspace from that list for now.
  const activeWorkspace = useMemo(
    () =>
      memberWorkspaces.find(
        (ws) => ws.id === dashboardWorkspaceContext.workspace.id
      ),
    [memberWorkspaces, dashboardWorkspaceContext.workspace.id]
  )

  const {
    isOpen,
    workspaceId,
    parentId,
    projectCode,
    workspaceName,
    fieldsToUpdate,
    isSameOrgOnly,
  } = useEditWorkspaceModalValues()
  const close = useCloseEditWorkspaceModal()
  const [isLoading, setIsLoading] = useState(false)

  const memberWorkspacesFiltered = useMemo(
    () =>
      memberWorkspaces?.filter((workspace) => {
        if (workspace.id === workspaceId) {
          return false
        }
        if (isSameOrgOnly && workspace.orgName !== activeWorkspace?.orgName) {
          return false
        }
        return true
      }) ?? [],
    [memberWorkspaces, activeWorkspace, isSameOrgOnly, workspaceId]
  )

  const updateWorkspace = useUpdateWorkspaceMutation(
    dashboardWorkspaceContext.owner.memberId
  )

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const {
    formState: { isDirty, errors },
  } = form

  useEffect(() => {
    const targetParent = memberWorkspaces.find((ws) => ws.id === parentId)
    form.reset({
      parent: targetParent
        ? {
            id: targetParent.id,
            commonName: targetParent.commonName,
          }
        : null,
      workspaceName,
      projectCode: projectCode || '',
    })
  }, [memberWorkspaces, parentId, workspaceName, projectCode])

  const handleSave = useCallback(
    async (values: FormSchema) => {
      if (!isDirty) {
        close()
        return
      }
      setIsLoading(true)
      updateWorkspace.mutateAsync(
        {
          id: workspaceId,
          workspaceName: fieldsToUpdate.includes('name')
            ? values.workspaceName
            : workspaceName,
          ...(fieldsToUpdate.includes('parent')
            ? {
                parentId:
                  values.parent === null
                    ? dashboardWorkspaceContext.memberId
                    : values.parent.id,
              }
            : {}),
          ...(fieldsToUpdate.includes('projectCode')
            ? {
                setupData: {
                  billingCode: values.projectCode,
                },
              }
            : {}),
        },
        {
          onSuccess() {
            close()
            setIsLoading(false)
          },
          onError() {
            setIsLoading(false)
          },
        }
      )
    },
    [
      isDirty,
      workspaceId,
      parentId,
      workspaceName,
      projectCode,
      dashboardWorkspaceContext.workspace.id,
    ]
  )

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        close()
        form.reset(defaultValues)
      }
    },
    [close]
  )

  const isDisabled = useMemo(
    () => !!(isLoading || !isDirty || errors.workspaceName),
    [isLoading, isDirty, errors.workspaceName]
  )

  const titleSuffix = useMemo(() => {
    if (fieldsToUpdate.length === 0 || fieldsToUpdate.length > 1) {
      return ''
    } else if (fieldsToUpdate[0] === 'parent') {
      return 'parent'
    } else if (fieldsToUpdate[0] === 'name') {
      return 'name'
    } else if (fieldsToUpdate[0] === 'projectCode') {
      return 'project code'
    }
  }, [fieldsToUpdate])

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="px-5 py-5 w-[37.5rem]" placement="center">
        <DialogHeader className="flex gap-4 items-center">
          <div className="bg-[#F4F6F7] p-2 rounded-lg">
            <EditWorkspaceIcon className="w-6" />
          </div>
          <DialogTitle className="text-black text-lg font-medium pr-4">
            {t('workspace.editWorkspace')} {titleSuffix}
          </DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col h-full"
          onSubmit={form.handleSubmit(handleSave)}
        >
          {fieldsToUpdate.includes('parent') ? (
            <Controller
              name="parent"
              control={form.control}
              render={({ field }) => {
                const { ref, ...remaining } = field
                return (
                  <div className="mt-4">
                    {/* Show label only if editing more than one fields */}
                    {fieldsToUpdate.length > 1 ? (
                      <label
                        htmlFor="parent"
                        className="text-[#707070] text-sm"
                      >
                        Parent workspace (optional)
                      </label>
                    ) : null}
                    <SearchableInputField<WorkspaceListingItem | null>
                      {...remaining}
                      inputFieldRef={ref}
                      id="parent"
                      inputContainerClassName="mt-2"
                      placeholder={t('label.none')}
                      options={memberWorkspacesFiltered}
                      keyGetter={(option) => option?.id ?? ''}
                      labelGetter={(option) => option?.commonName ?? ''}
                      nullable
                    />
                    {errors.parent ? (
                      <p className="text-sm text-red-500 mt-2">
                        {errors.parent.message}
                      </p>
                    ) : null}
                  </div>
                )
              }}
            />
          ) : null}

          {fieldsToUpdate.includes('name') ? (
            <div className="mt-4">
              {/* Show label only if editing more than one fields */}
              {fieldsToUpdate.length > 1 ? (
                <label
                  htmlFor="workspaceName"
                  className="text-[#707070] text-sm"
                >
                  Workspace name (required)
                </label>
              ) : null}
              <input
                id="workspaceName"
                type="text"
                className="w-full p-3.5 mt-2 border border-[#D6D9DC] rounded-md resize-none placeholder:text-[#A9A9A9] text-sm"
                placeholder="Enter workspace name"
                {...form.register('workspaceName')}
              />
              {errors.workspaceName ? (
                <p className="text-sm text-red-500 mt-2">
                  {errors.workspaceName.message}
                </p>
              ) : null}
            </div>
          ) : null}

          {fieldsToUpdate.includes('projectCode') ? (
            <div className="mt-4">
              {/* Show label only if editing more than one fields */}
              {fieldsToUpdate.length > 1 ? (
                <label htmlFor="projectCode" className="text-[#707070] text-sm">
                  Project code (optional)
                </label>
              ) : null}
              <input
                id="projectCode"
                type="text"
                className="w-full p-3.5 mt-2 border border-[#D6D9DC] rounded-md resize-none placeholder:text-[#A9A9A9] text-sm"
                placeholder="Enter project code"
                {...form.register('projectCode')}
              />
              {errors.projectCode ? (
                <p className="text-sm text-red-500 mt-2">
                  {errors.projectCode.message}
                </p>
              ) : null}
            </div>
          ) : null}

          <footer className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center mt-10 gap-2">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <DialogClose asChild>
                <button
                  type="button"
                  onClick={() => close()}
                  className="px-7 py-3 rounded-xl border border-[#707070] text-[#707070] hover:border-gray-400 hover:text-gray-400 transition-colors font-semibold text-sm cursor-pointer"
                >
                  {t('action.cancel')}
                </button>
              </DialogClose>
              <button
                className="px-7 py-[0.8rem] rounded-xl text-white bg-primary-500 hover:bg-blue-500 transition-colors font-semibold text-sm cursor-pointer disabled:bg-blue-300 disabled:cursor-not-allowed"
                type="submit"
                disabled={isDisabled}
              >
                {t('action.save')}
              </button>
            </div>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  )
}
